import { Controller } from '@hotwired/stimulus';

/**
 * Applies a text case transformation to every text input and textarea inside
 * the controlled element, both while typing and when the field loses focus.
 *
 *   data-controller="input-case"
 *   data-input-case-mode-value="capitalize"   → Title Case (Nombre Apellido)
 *   data-input-case-mode-value="uppercase"     → All uppercase
 *   data-input-case-mode-value="lowercase"     → All lowercase
 *   (omit or leave empty to disable)
 *
 * To exempt an individual input from transformation:
 *   data-input-case-skip="true"
 */
export default class extends Controller {

    static values = { mode: String };
    declare readonly modeValue: string;

    connect(): void {
        if (!this.modeValue) return;
        this.element.addEventListener('input', this.onInput, true);
        this.element.addEventListener('focusout', this.onFocusOut, true);
    }

    disconnect(): void {
        this.element.removeEventListener('input', this.onInput, true);
        this.element.removeEventListener('focusout', this.onFocusOut, true);
    }

    private onInput = (event: Event): void => {
        const target = this.eligibleTarget(event.target);
        if (!target) return;

        const pos = target.selectionStart;
        const val = target.value;
        const transformed = this.transform(val);

        if (transformed !== val) {
            target.value = transformed;
            // Case-only changes keep the length, so the caret position is preserved.
            if (pos !== null) {
                const p = Math.min(pos, transformed.length);
                try { target.setSelectionRange(p, p); } catch { /* unsupported input type */ }
            }
        }
    };

    private onFocusOut = (event: Event): void => {
        const target = this.eligibleTarget(event.target);
        if (!target) return;

        const val = target.value;
        const transformed = this.transform(val);
        if (transformed !== val) {
            target.value = transformed;
            // Let listeners (dirty-form, validation, ...) react to the normalised value.
            target.dispatchEvent(new Event('change', { bubbles: true }));
        }
    };

    private eligibleTarget(node: EventTarget | null): HTMLInputElement | HTMLTextAreaElement | null {
        if (!(node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement)) return null;
        if (node instanceof HTMLInputElement
            && node.type
            && !['text', 'email', 'search', 'tel', 'url', ''].includes(node.type)) return null;
        if (node.dataset['inputCaseSkip'] === 'true') return null;
        return node;
    }

    private transform(value: string): string {
        switch (this.modeValue) {
            case 'uppercase':
                return value.toUpperCase();
            case 'lowercase':
                return value.toLowerCase();
            case 'capitalize':
                // Title Case: lowercase everything, then uppercase the first letter of
                // each word. \p{L} (with the `u` flag) covers accented initials such as
                // Á, É, Ñ; word starts are detected after spaces and name separators.
                return value
                    .toLowerCase()
                    .replace(/(^|[\s\-'/])(\p{L})/gu, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
            default:
                return value;
        }
    }
}
